import { useNavigate, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from '../../i18n/useTranslation';
import { searchParamsSchema, type ParsedSearchParams } from '../data/schemas';
import wizardService from '../domain/wizardService';
import type { FilterOption, WizardStepName } from '../domain/models';
import routes from '../../routing/routes';

export type WizardPageViewModel = ReturnType<typeof useWizardPageViewModel>;
export function useWizardPageViewModel() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const currentStep = parseSearchParams(searchParams);

  const optionsQuery = useQuery({
    queryKey: ['wizardOptions', currentStep],
    queryFn: async () => {
      const data = currentStep
        ? await getOptionsForStep(currentStep)
        : [];
      return {
        stepName: currentStep?.stepName,
        options: data,
      };
    },
  });

  const getOptionsForStep = async (step: ParsedSearchParams) => {
    switch (step.stepName) {
      case 'flavourSelect':
        return wizardService.getFlavourOptions(step.category, step.flavours);
      case 'tasteSelect':
        return wizardService.getTasteOptions(step.category, step.flavours, step.tastes);
      case 'mouthfeelSelect':
        return wizardService.getMouthfeelOptions(step.category, step.flavours, step.tastes, step.mouthfeels);
      default:
        return Promise.resolve([]);
    }
  };

  const setOption = (option: FilterOption, checked: boolean) => {
    if (!currentStep) {
      return;
    }
    if (currentStep.stepName == 'flavourSelect') {
      const flavourIds = toggleOption(currentStep.flavours, option.id, checked);
      return navigate(routes.createWizardFlavourUrl(currentStep.category, flavourIds));
    }
    if (currentStep.stepName == 'tasteSelect') {
      const tasteIds = toggleOption(currentStep.tastes, option.id, checked);
      return navigate(routes.createWizardTasteUrl(currentStep.category, currentStep.flavours, tasteIds));
    }
    if (currentStep.stepName == 'mouthfeelSelect') {
      const mouthfeelIds = toggleOption(currentStep.mouthfeels, option.id, checked);
      return navigate(routes.createWizardMouthfeelUrl(currentStep.category, currentStep.flavours, currentStep.tastes, mouthfeelIds));
    }
    console.warn('Unknown step', currentStep);
  };

  const goToPreviousStep = () => {
    if (!currentStep) {
      return;
    }
    switch (currentStep.stepName) {
      case 'flavourSelect':
        return navigate(routes.createWizardCategoryUrl());
      case 'tasteSelect':
        return navigate(routes.createWizardFlavourUrl(currentStep.category, currentStep.flavours));
      case 'mouthfeelSelect':
        return navigate(routes.createWizardTasteUrl(currentStep.category, currentStep.flavours, currentStep.tastes));
      default:
        return;
    }
  };

  const gotToNextStep = () => {
    if (currentStep?.stepName === 'flavourSelect') {
      return navigate(routes.createWizardTasteUrl(currentStep.category, currentStep.flavours, []));
    }
    if (currentStep?.stepName === 'tasteSelect') {
      return navigate(routes.createWizardMouthfeelUrl(currentStep.category, currentStep.flavours, currentStep.tastes, []));
    }
    if (currentStep?.stepName === 'mouthfeelSelect') {
      return navigate(routes.createResultsPageUrl(currentStep.category, currentStep.flavours, currentStep.tastes, currentStep.mouthfeels));
    }
    console.warn('Unknown step', currentStep);
  };

  const getStepTitle = () => {
    if (!currentStep) {
      return '';
    }
    const map: Record<WizardStepName, string> = {
      categorySelect: '',
      flavourSelect: t('flavour_title'),
      tasteSelect: t('taste_title'),
      mouthfeelSelect: t('mouthfeel_title'),
    };
    return map[currentStep.stepName];
  };

  return {
    isLoadingInitialOptions: optionsQuery.isFetching && optionsQuery.data?.stepName !== currentStep?.stepName,
    isLoadingNextOptions: optionsQuery.isFetching && optionsQuery.data?.stepName === currentStep?.stepName,
    error: optionsQuery.error,

    currentStep,
    stepTitle: getStepTitle(),
    options: optionsQuery.data?.options || [],
    isLatestStep: currentStep?.stepName === 'mouthfeelSelect',

    setOption,
    goToPreviousStep,
    gotToNextStep,
  };
};

const parseSearchParams = (searchParams: URLSearchParams): ParsedSearchParams | null => {
  const searchParamsObject = Object.fromEntries(searchParams.entries());
  const parsed = searchParamsSchema.safeParse(searchParamsObject);

  if (!parsed.success) {
    console.warn('Invalid search params', parsed.error);
    return null;
  }

  return parsed.data;
};

const toggleOption = (options: string[], optionId: string, checked: boolean) => {
  const newOptions = [...options];
  if (checked && !newOptions.includes(optionId)) {
    newOptions.push(optionId);
  }
  if (!checked) {
    const index = newOptions.indexOf(optionId);
    if (index > -1) {
      newOptions.splice(index, 1);
    }
  }
  return newOptions;
};
